import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  User, FileText, Users, CreditCard, Key, 
  ChevronRight, Settings as SettingsIcon 
} from "lucide-react";
import AOS from 'aos';
import 'aos/dist/aos.css';

import AddSettingForm from "../Setting/AccountDetail";
import AgreementForm from "../Setting/Agreement";
import TeamMemberForm from "../Setting/TeamMembers";
import TaxInfoForm from "../Setting/TaxInfo";
import ApiKeyForm from "../Setting/ApiKey";
import { useAuth } from "../Auth/AuthContext";

const Setting = () => {
  const { authState } = useAuth();
  const role = authState.roles;
  const admin = role === "Admin";

  const [activeSection, setActiveSection] = useState("form");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    AOS.init({ 
      duration: 800, 
      once: true,
      offset: 50
    });
  }, []);

  const sections = [
    { 
      id: "form", 
      label: "Account Details", 
      icon: <User size={18} />,
      description: "Update your personal information"
    },
    { 
      id: "agreement", 
      label: "Agreement", 
      icon: <FileText size={18} />,
      description: "View and manage your agreements"
    },
    admin && { 
      id: "team", 
      label: "Team Members", 
      icon: <Users size={18} />,
      description: "Manage team access and permissions"
    },
    { 
      id: "taxInfo", 
      label: "Tax Information", 
      icon: <CreditCard size={18} />,
      description: "Configure tax details and documents"
    },
    { 
      id: "apiKey", 
      label: "API Key", 
      icon: <Key size={18} />,
      description: "Manage your API credentials"
    },
  ].filter(Boolean);

  const sectionComponents = {
    form: <AddSettingForm />,
    agreement: <AgreementForm />,
    team: <TeamMemberForm />,
    taxInfo: <TaxInfoForm />,
    apiKey: <ApiKeyForm />,
  };

  return (
    <div className="min-h-full w-full pb-8 mt-6">
      {/* Header */}
      <div 
        className="bg-white shadow-sm rounded-xl border border-gray-200 p-6 mb-6"
        data-aos="fade-down"
      >
        <div className="flex items-center gap-3 mb-2">
          <SettingsIcon className="text-indigo-600" size={24} />
          <h2 className="text-2xl font-semibold text-gray-800">Settings</h2>
        </div>
        <p className="text-sm text-gray-500 max-w-2xl">
          Manage your account preferences, security settings, and integration configurations.
          {admin && " As an admin, you have access to additional team management features."}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Mobile Menu Button (hidden on desktop) */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden flex items-center justify-between w-full p-4 bg-white border border-gray-200 rounded-xl shadow-sm mb-4"
        >
          <div className="flex items-center gap-3">
            {sections.find(s => s.id === activeSection)?.icon}
            <span className="font-medium">
              {sections.find(s => s.id === activeSection)?.label}
            </span>
          </div>
          <ChevronRight 
            size={18} 
            className={`transition-transform ${mobileMenuOpen ? 'rotate-90' : ''}`}
          />
        </button>

        {/* Vertical Tabs */}
        <div 
          className={`${mobileMenuOpen ? 'block' : 'hidden'} lg:block w-full lg:w-72 shrink-0`}
          data-aos="fade-right"
        >
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-medium text-gray-700">Settings Menu</h3>
            </div>
            <nav className="flex flex-col gap-1 p-2">
              {sections.map(({ id, label, icon, description }) => (
                <motion.button
                  key={id}
                  onClick={() => {
                    setActiveSection(id);
                    setMobileMenuOpen(false);
                  }}
                  className={`text-left p-3 rounded-lg transition-all duration-200 flex items-start gap-3 ${
                    activeSection === id
                      ? "bg-indigo-50 border border-indigo-100"
                      : "hover:bg-gray-50"
                  }`}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <div className={`p-2 rounded-md ${
                    activeSection === id ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {icon}
                  </div>
                  <div className="flex-1">
                    <div className={`font-medium ${
                      activeSection === id ? 'text-indigo-700' : 'text-gray-700'
                    }`}>{label}</div>
                    <div className="text-xs text-gray-500 mt-1">{description}</div>
                  </div>
                  {activeSection === id && (
                    <div className="w-2 h-2 rounded-full bg-indigo-500 mt-2" />
                  )}
                </motion.button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content Panel */}
        <div className="flex-1" data-aos="fade-up" data-aos-delay="100">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden min-h-[500px]"
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`p-2 rounded-md bg-indigo-100 text-indigo-600`}>
                    {sections.find(s => s.id === activeSection)?.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    {sections.find(s => s.id === activeSection)?.label}
                  </h3>
                </div>
                {sectionComponents[activeSection]}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Setting;